import { Component, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { UsersService } from '../../../shared/services/users.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    PasswordModule,
    ButtonModule,
    ImageModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm: FormGroup;
  isLoading = false;
  errorMessage: string = '';
  resetId: string = '';

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private usersService: UsersService,
    private messageService: MessageService
  ) {
    this.resetPasswordForm = this.fb.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    });
  }

  get password() {
    return this.resetPasswordForm.get('password');
  }

  get confirmPassword() {
    return this.resetPasswordForm.get('confirmPassword');
  }

  ngOnInit() {
    // Extract the ID from the URL query parameters
    // First check snapshot for immediate access (initial load)
    const snapshotParams = this.route.snapshot.queryParams;
    this.resetId = snapshotParams['id'] || '';
    
    // If no ID found in snapshot, show error and redirect immediately
    if (!this.resetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid reset link. Please request a new password reset.',
        life: 5000,
      });
      // Redirect to forgot password page
      this.router.navigate(['/forgot-password']);
      return;
    }
    
    // Also subscribe to query params for navigation changes (if user navigates with different params)
    this.route.queryParams.subscribe((params) => {
      this.resetId = params['id'] || '';
      if (!this.resetId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Invalid reset link. Please request a new password reset.',
          life: 5000,
        });
        // Redirect to forgot password page
        this.router.navigate(['/forgot-password']);
      }
    });
  }

  onSubmit(): void {
    if (this.resetPasswordForm.valid && this.resetId) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.resetPasswordForm.value;

      // Call the reset password API
      this.usersService
        .resetPassword(
          this.resetId,
          formValue.password,
          formValue.confirmPassword,
          ''
        )
        .subscribe({
          next: (response) => {
            this.isLoading = false;

            // Show success toaster
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail:
                response.message || 'Password has been reset successfully.',
              life: 5000,
            });

            // Navigate to sign-in page after a short delay
            setTimeout(() => {
              this.router.navigate(['/sign-in']);
            }, 2000);
          },
          error: (error) => {
            this.isLoading = false;

            // Show error toaster
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                error.error?.message ||
                'Failed to reset password. Please try again.',
              life: 5000,
            });
          },
        });
    } else {
      this.resetPasswordForm.markAllAsTouched();
    }
  }
}
